import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import addWeeks from 'date-fns/addWeeks';
import isAfter from 'date-fns/isAfter';
import isValid from 'date-fns/isValid';
import subWeeks from 'date-fns/subWeeks';
import DatePicker from 'react-datepicker';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/free-solid-svg-icons';

import 'react-datepicker/dist/react-datepicker.css';

export default memo(CustomFilterModalDisplay);

function CustomFilterModalDisplay(props) {
  const {
    endDate,
    globalMaxDatapoints,
    handleChangeMaxDatapoints,
    handleChangeStart,
    handleChangeEnd,
    handleSave,
    isOpen,
    isTextTile,
    maxDatapoints,
    startDate,
    toggleModal,
  } = props;

  const now = useMemo(() => new Date(), []),
    startMinDate = useMemo(() => {
      if (!isTextTile) return null;
      return isValid(endDate) ? subWeeks(endDate, 1) : null;
    }, [endDate, isTextTile]),
    endMaxDate = useMemo(() => {
      if (!isTextTile || !isValid(startDate)) return now;
      const oneWeekAhead = addWeeks(startDate, 1);
      return isAfter(oneWeekAhead, now) ? now : oneWeekAhead;
    }, [isTextTile, now, startDate]);

  return (
    <Modal
      centered
      className="rounded-lg"
      isOpen={isOpen}
      size="md"
      toggle={toggleModal}
    >
      <ModalHeader className="border-bottom-0" toggle={toggleModal}>
        Filter Data
      </ModalHeader>
      <ModalBody className="px-3 py-4">
        <p
          className={
            isTextTile ? 'text-left modal-supplemental-text mb-5' : 'd-none'
          }
        >
          Choose a range in the past of up to 1 week. The most recent value
          within that range for each signal will be retrieved. If no data is
          available within that range a message will be displayed.
        </p>
        <div className="d-flex flex-column flex-md-row justify-content-between">
          <div className="form-group d-flex flex-column">
            <label
              className="d-block modal-inputlabel-text"
              htmlFor="startDate"
            >
              Start Date
            </label>
            <DatePicker
              allowSameDay
              autoComplete="off"
              className="form-control"
              dateFormat={[
                'MMM d, yyyy H:mm',
                'P',
                'PP',
                'PPP',
                'MMM dd yyyy',
                'MMMM dd yyyy',
                'MM/yyyy',
              ]}
              endDate={endDate}
              name="startDate"
              minDate={startMinDate}
              maxDate={endDate ?? now}
              onChange={handleChangeStart}
              placeholderText="From"
              selected={startDate}
              selectsStart
              showTimeSelect
              showYearDropdown
              startDate={startDate}
              timeCaption="time"
              timeFormat="HH:mm"
              timeIntervals={15}
            />
          </div>
          <div className="d-none d-md-flex align-items-center justify-content-center">
            <FontAwesomeIcon icon={faMinus} />
          </div>
          <hr className="d-md-none" />
          <div className="form-group d-flex flex-column">
            <label className="d-block modal-inputlabel-text" htmlFor="endDate">
              End Date
            </label>
            <DatePicker
              allowSameDay
              autoComplete="off"
              className="form-control"
              dateFormat={[
                'MMM d, yyyy H:mm',
                'P',
                'PP',
                'PPP',
                'MMM dd yyyy',
                'MMMM dd yyyy',
                'MM/yyyy',
              ]}
              endDate={endDate}
              name="endDate"
              onChange={handleChangeEnd}
              minDate={startDate}
              maxDate={endMaxDate}
              placeholderText="To"
              selected={endDate}
              selectsEnd
              showTimeSelect
              showYearDropdown
              startDate={startDate}
              timeCaption="time"
              timeFormat="HH:mm"
              timeIntervals={15}
            />
          </div>
        </div>
        {isTextTile ? null : (
          <div className="form-group mt-4">
            <label className="modal-inputlabel-text" htmlFor="datapoints">
              Datapoints
              <small className="text-muted">{` (Max of ${globalMaxDatapoints})`}</small>
            </label>
            <input
              className="form-control"
              max={globalMaxDatapoints}
              min="1"
              name="datapoints"
              onChange={handleChangeMaxDatapoints}
              type="number"
              value={maxDatapoints}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter className="border-top-0">
        <button
          className="btn btn-outline-secondary py-0 px-3 mr-3 three-quarter-text"
          onClick={toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit py-0 px-4 three-quarter-text"
          onClick={handleSave}
        >
          Update
        </button>
      </ModalFooter>
    </Modal>
  );
}

CustomFilterModalDisplay.propTypes = {
  endDate: PropTypes.object,
  globalMaxDatapoints: PropTypes.number,
  handleChangeEnd: PropTypes.func,
  handleChangeMaxDatapoints: PropTypes.func,
  handleChangeStart: PropTypes.func,
  handleSave: PropTypes.func,
  isOpen: PropTypes.bool,
  isTextTile: PropTypes.bool,
  maxDatapoints: PropTypes.number,
  startDate: PropTypes.object,
  toggleModal: PropTypes.func,
};
